import type * as React from "react";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { DateFilter } from "../../../../date-filter";
import { NumericRangeInput } from "../../../../numeric-range-input";
import type { BaseComponentProps, QuerySchema } from "../types";
import { TextFilter } from "./text-filter";

export interface QueryFiltersProps extends BaseComponentProps {
	/** Query schema containing field definitions */
	schema: QuerySchema;
	/** Current filter values */
	values: Record<string, any>;
	/** Callback when any filter value changes */
	onChange: (fieldId: string, value: any) => void;
}

export function QueryFilters({
	schema,
	values,
	onChange,
	disabled = false,
}: QueryFiltersProps): React.JSX.Element | null {
	// Don't render if no fields
	if (!schema.fields || schema.fields.length === 0) {
		return null;
	}

	const handleFilterChange = (fieldId: string, value: any) => {
		onChange(fieldId, value);
	};

	return (
		<Stack spacing={2}>
			<Typography
				variant="overline"
				color="text.secondary">
				Filters
			</Typography>
			<Stack spacing={2}>
				{schema.fields.map((field) => {
					if (field.type === "text") {
						return (
							<TextFilter
								key={field.id}
								field={field}
								value={values[field.id] ?? []}
								onChange={(value) => handleFilterChange(field.id, value)}
								disabled={disabled}
							/>
						);
					}

					if (field.type === "numeric") {
						return (
							<NumericRangeInput
								key={field.id}
								label={field.displayName}
								value={values[field.id] ?? [field.min ?? 0, field.max ?? 100]}
								onChange={(value) => handleFilterChange(field.id, value)}
								min={field.min ?? 0}
								max={field.max ?? 100}
								defaultMin={field.min ?? 0}
								defaultMax={field.max ?? 100}
								showSlider={false}
								allowSliderToggle={true}
								size="small"
								disabled={disabled}
							/>
						);
					}

					if (field.type === "date") {
						return (
							<DateFilter
								key={field.id}
								field={field}
								value={
									values[field.id] ?? {
										startDate: field.minDate ?? "",
										endDate: field.maxDate ?? "",
									}
								}
								onChange={(value) => handleFilterChange(field.id, value)}
								defaultStartDate={field.minDate ?? ""}
								defaultEndDate={field.maxDate ?? ""}
								disabled={disabled}
							/>
						);
					}

					return null;
				})}
			</Stack>
		</Stack>
	);
}
